/**
 * ThreadRow
 * One conversation in the email inbox list: unread dot, who it is with, a Tenant / Vendor type
 * badge per counterparty kind (BR-117), time, subject and a one-line preview.
 */

import { asIsoDateTimeString } from '@resida/shared';
import { Badge } from '../ui/badge';
import { formatTimestamp, deriveTimezone } from '../../utils/date';
import type { ThreadSummary, CounterpartyKind } from '../../lib/api';

const DAY_KEY: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'numeric', day: 'numeric' };
const DAY_MS = 24 * 60 * 60 * 1000;

/** Compact list timestamp in the landlord's timezone: time today, weekday this week, else month/day. */
function formatCompactDate(dateStr: string, timezone: string): string {
  const iso = asIsoDateTimeString(dateStr);
  const now = new Date();
  if (
    formatTimestamp(iso, timezone, DAY_KEY) ===
    formatTimestamp(asIsoDateTimeString(now.toISOString()), timezone, DAY_KEY)
  ) {
    return formatTimestamp(iso, timezone, { hour: 'numeric', minute: '2-digit' });
  }
  if (new Date(dateStr).getTime() >= now.getTime() - 6 * DAY_MS) {
    return formatTimestamp(iso, timezone, { weekday: 'short' });
  }
  return formatTimestamp(iso, timezone, { month: 'short', day: 'numeric' });
}

/** Who the thread is with: the filed counterparties, or the outside addresses for an unrecognized one. */
function threadDisplayName(thread: ThreadSummary): string {
  return thread.counterparties.length > 0
    ? thread.counterparties.map((c) => c.name).join(', ')
    : thread.participants.join(', ');
}

interface TypeBadge {
  kind: CounterpartyKind;
  label: string;
}

/** `Tenant · <unit>`, `Tenants · <unit>` for several, `Vendor · <trade>` — one badge per kind present. */
function typeBadges(thread: ThreadSummary): TypeBadge[] {
  return (['tenant', 'vendor'] as const).flatMap((kind) => {
    const group = thread.counterparties.filter((c) => c.kind === kind);
    if (group.length === 0) return [];
    const noun = kind === 'tenant' ? 'Tenant' : 'Vendor';
    const subtitle = group.find((c) => c.subtitle)?.subtitle;
    return [{ kind, label: `${noun}${group.length > 1 ? 's' : ''}${subtitle ? ` · ${subtitle}` : ''}` }];
  });
}

interface ThreadRowProps {
  thread: ThreadSummary;
  isLast: boolean;
  showSpam: boolean;
  onSelect: (threadId: string) => void;
}

export function ThreadRow({ thread, isLast, showSpam, onSelect }: ThreadRowProps): JSX.Element {
  const isUnread = thread.unread;
  const name = threadDisplayName(thread);
  const badges = typeBadges(thread);
  const timezone = deriveTimezone('');
  return (
    <button
      onClick={() => onSelect(thread.threadId)}
      className={`w-full text-left px-4 py-3 min-h-[44px] transition-colors ${
        isLast ? '' : 'border-b border-border'
      } hover:bg-accent ${isUnread ? 'bg-card' : 'bg-card/60'}`}
      data-testid={`thread-${thread.threadId}`}
      aria-label={`${isUnread ? 'Unread: ' : ''}Conversation with ${name}, ${thread.subject || '(No subject)'}`}
    >
      <div className="flex items-start gap-3">
        {/* Unread indicator — colour is decorative only; the accessible name lives on the
            enclosing button's aria-label above. */}
        <div className="mt-[7px] shrink-0 w-2 h-2">
          {isUnread && (
            <span
              className="block w-2 h-2 rounded-full bg-primary"
              data-testid="unread-dot"
              aria-hidden="true"
            />
          )}
        </div>

        <div className="flex-1 min-w-0 space-y-0.5">
          {/* Name, type badges (own line on a phone) and time */}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span
              className={`min-w-0 truncate text-sm ${isUnread ? 'font-semibold text-foreground' : 'font-medium text-foreground/80'}`}
            >
              {name}
            </span>
            <span className="order-last basis-full flex flex-wrap gap-1.5 md:order-none md:basis-auto">
              {badges.map((badge) => (
                <Badge
                  key={badge.kind}
                  variant={badge.kind === 'vendor' ? 'info' : 'secondary'}
                  data-testid={`thread-type-badge-${badge.kind}`}
                >
                  {badge.label}
                </Badge>
              ))}
              {showSpam && thread.spamFlagged && (
                <Badge variant="warning" data-testid="thread-possible-spam">
                  Possible spam
                </Badge>
              )}
            </span>
            <span className="ml-auto text-xs text-muted-foreground shrink-0 tabular-nums">
              {formatCompactDate(thread.lastMessage.createdAt, timezone)}
            </span>
          </div>

          <div
            className={`text-sm truncate leading-snug ${isUnread ? 'font-semibold text-foreground' : 'font-normal text-foreground/70'}`}
          >
            {thread.subject || '(No subject)'}
          </div>

          <div className="text-xs text-muted-foreground truncate leading-relaxed">
            {thread.lastMessage.bodyText ? thread.lastMessage.bodyText.slice(0, 120) : ' '}
          </div>
        </div>
      </div>
    </button>
  );
}
